import type { IAgentImportResult } from "../types";

interface IImportInBatchesProps<TAgent extends { agentId: string }> {
  agents: TAgent[];
  batchSize: number;
  sendBatch: (batch: TAgent[]) => Promise<IAgentImportResult[]>;
}

// NOTE: NSAI-6583 / NSAI-6677 — the server caps one import request, and a
// migration is often larger, so every import source sends its selection in
// sequential batches and hands back one combined result list. Sequential, not
// parallel: the server's duplicate-name check would race across concurrent
// batches. A failed first batch rethrows (nothing was confirmed); a later
// failure keeps the confirmed rows and marks the rest `isOutcomeUnknown`, so
// the user still sees which assistants were already created instead of a bare
// error.
export const importInBatches = async <TAgent extends { agentId: string }>({
  agents,
  batchSize,
  sendBatch,
}: IImportInBatchesProps<TAgent>): Promise<IAgentImportResult[]> => {
  const results: IAgentImportResult[] = [];

  for (let start = 0; start < agents.length; start += batchSize) {
    const batch = agents.slice(start, start + batchSize);
    try {
      results.push(...(await sendBatch(batch)));
    } catch (error) {
      if (start === 0) {
        throw error;
      }
      const unconfirmed = agents.slice(start).map((agent) => ({
        agentId: agent.agentId,
        success: false,
        isOutcomeUnknown: true,
      }));
      return [...results, ...unconfirmed];
    }
  }

  return results;
};
